import dayjs, { Dayjs, ManipulateType } from "dayjs";
import { TimeFrame, TimeRange } from "src/components/monitoring/commonTypes";

/**
 * Returns new date with seconds and milliseconds set to 0
 *
 * @param date date to transform
 * @returns transformed date
 */
export const toMinutePrecision = (date: Dayjs) => {
    return date.second(0).millisecond(0);
};

/**
 * Returns boolean indicating if given date is current date - today
 * Ignores seconds and milliseconds in check
 *
 * @param date date to check
 * @returns boolean
 */
export const isToday = (date: Dayjs) => {
    return toMinutePrecision(date).isSame(toMinutePrecision(dayjs()));
};

/**
 * Returns boolean indicating if given date is before today
 * Ignores seconds and milliseconds in check
 *
 * @param date date to check
 * @returns boolean
 */
export const isBeforeToday = (date: Dayjs) => toMinutePrecision(date).isBefore(toMinutePrecision(dayjs()));

/**
 * Returns boolean indicating if start date is before end date
 * of the given timeFrame
 * Ignores seconds and milliseconds in check
 *
 * @param timeFrame to check
 * @returns boolean
 */
export const isValidInterval = (timeFrame: TimeFrame) =>
    toMinutePrecision(dayjs(timeFrame.start)).isBefore(toMinutePrecision(dayjs(timeFrame.end)));

/**
 * Returns boolean indicating if start and end date are before today
 * and start date is before end date
 *
 * @param timeFrame to check
 * @returns boolean
 */
export const isValidTimeFrame = (timeFrame: TimeFrame, disableFuture = true) =>
    (isBeforeToday(dayjs(timeFrame.start)) || isToday(dayjs(timeFrame.start))) &&
    (!disableFuture || isBeforeToday(dayjs(timeFrame.end)) || isToday(dayjs(timeFrame.end))) &&
    isValidInterval(timeFrame);

/**
 * Returns valid timeFrame - validates given timeFrame and, if necessary, changes it to be valid,
 * otherwise returns fallbackTimeFrame
 *
 * @param timeFrame to validate
 * @param fallbackTimeFrame valid timeFrame to use in case given timeFrame is invalid
 * @returns valid timeFrame
 */
export const getValidTimeFrame = (timeFrame: TimeFrame, fallbackTimeFrame: TimeFrame, futureDisabled = true) => {
    const validTimeFrame = { start: dayjs(timeFrame.start), end: dayjs(timeFrame.end) };
    if (!isBeforeToday(validTimeFrame.end) && futureDisabled) {
        validTimeFrame.end = dayjs();
    }
    return !isBeforeToday(validTimeFrame.start) || !isValidInterval(validTimeFrame) ? fallbackTimeFrame : validTimeFrame;
};

/**
 * Returns timeFrame in relation to the current date, based on the given timeRange
 * Returns undefined if timeRange is FREE
 *
 * @param timeRange timeRange used to calculate timeFrame
 * @returns timeFrame
 */
export const getLatestTimeFrame = (timeRange: TimeRange, disableFuture = true) => {
    const now = dayjs();

    switch (timeRange) {
        case TimeRange.FREE:
            return;
        case TimeRange.CUSTOM:
            return;
        case TimeRange.ONE_MINUTE:
            return { start: now.startOf("minute").subtract(1, "minute"), end: now };
        case TimeRange.FIVE_MINUTES:
            return { start: now.startOf("minute").subtract(5, "minutes"), end: now };
        case TimeRange.ONE_HOUR:
            return { start: now.subtract(1, "hour"), end: now };
        case TimeRange.THREE_HOURS:
            return { start: now.subtract(3, "hour"), end: now };
        case TimeRange.TWENTY_FOUR_HOURS:
            return { start: now.subtract(24, "hour"), end: now };
        case TimeRange.YESTERDAY:
            return {
                start: now.subtract(1, "day").startOf("day"),
                end: now.subtract(1, "day").endOf("day"),
            };
        case TimeRange.TWO_DAYS:
            return { start: now.startOf("day").subtract(2, "day"), end: now };
        case TimeRange.SEVEN_DAYS:
            return { start: now.startOf("day").subtract(7, "day"), end: now };
        case TimeRange.FOURTEEN_DAYS:
            return { start: now.startOf("day").subtract(14, "day"), end: now };
        case TimeRange.THIRTY_DAYS:
            return { start: now.startOf("day").subtract(30, "day"), end: now };
        case TimeRange.THREE_HUNDRED_65_DAYS:
            return { start: now.startOf("day").subtract(365, "day"), end: now };
        case TimeRange.DAY:
            return { start: now.startOf("day"), end: disableFuture ? now : now.endOf("day") };
        case TimeRange.TODAY:
            return { start: now.startOf("day"), end: disableFuture ? now : now.endOf("day") };
        case TimeRange.WEEK:
            return { start: now.startOf("isoWeek"), end: disableFuture ? now : now.endOf("isoWeek") };
        case TimeRange.MONTH:
            return { start: now.startOf("month"), end: disableFuture ? now : now.endOf("month") };
        case TimeRange.YEAR:
            return { start: now.startOf("year"), end: disableFuture ? now : now.endOf("year") };
        case TimeRange.YEARS:
            return {
                start: dayjs("2000-01-01T00:00:00.000Z"),
                end: disableFuture ? now : now.endOf("year"),
            };
    }
};

/**
 * Returns previous timeFrame in relation to the given time frame, based on the given timeRange
 *
 * @param timeRange timeRange used to calculate timeFrame
 * @param timeFrame timeFrame used to calculate previous timeFrame
 * @returns prev timeFrame
 */
export const getPrevTimeFrame = (timeRange: TimeRange, timeFrame: TimeFrame, offset: number) => {
    const endDate = dayjs(timeFrame.start).subtract(offset, "minute");

    switch (timeRange) {
        case TimeRange.SEVEN_DAYS:
            return { start: dayjs(timeFrame.start).subtract(7, "day"), end: endDate };
        case TimeRange.THIRTY_DAYS:
            return { start: dayjs(timeFrame.start).subtract(30, "day"), end: endDate };
        case TimeRange.THREE_HUNDRED_65_DAYS:
            return { start: dayjs(timeFrame.start).subtract(365, "day"), end: endDate };
        case TimeRange.DAY:
            return { start: dayjs(timeFrame.start).subtract(1, "day"), end: endDate };
        case TimeRange.TODAY:
            return { start: dayjs(timeFrame.start).subtract(1, "day"), end: endDate };
        case TimeRange.YESTERDAY:
            return {
                start: dayjs(timeFrame.start).subtract(1, "day").startOf("day"),
                end: endDate.subtract(1, "day").endOf("day"),
            };
        case TimeRange.WEEK:
            return { start: dayjs(timeFrame.start).subtract(1, "week"), end: endDate };
        case TimeRange.MONTH:
            return { start: dayjs(timeFrame.start).subtract(1, "month"), end: endDate };
        case TimeRange.YEAR:
            return { start: dayjs(timeFrame.start).subtract(1, "year"), end: endDate };
        /**
         * Calculates prev timeFrame based on start/end date difference
         * for timeRanges smaller than DAY and timeRange FREE
         */
        default:
            return getSwitchTimeFrameCustomRange(timeFrame, false);
    }
};

/**
 * Returns next timeFrame in relation to the given time frame, based on the given timeRange
 *
 * @param timeRange timeRange used to calculate timeFrame
 * @param timeFrame timeFrame used to calculate next timeFrame
 * @returns next timeFrame
 */
export const getNextTimeFrame = (timeRange: TimeRange, timeFrame: TimeFrame, offset: number, disableFuture?: boolean) => {
    const now = dayjs();
    const startDate = dayjs(timeFrame.end).add(offset, "minute");
    let endDate;

    switch (timeRange) {
        case TimeRange.SEVEN_DAYS:
            endDate = dayjs(timeFrame.end).add(7, "day");
            break;
        case TimeRange.THIRTY_DAYS:
            endDate = dayjs(timeFrame.end).add(30, "day");
            break;
        case TimeRange.THREE_HUNDRED_65_DAYS:
            endDate = dayjs(timeFrame.end).add(365, "day");
            break;
        case TimeRange.DAY:
            endDate = dayjs(timeFrame.end).add(1, "day");
            break;
        case TimeRange.WEEK:
            endDate = dayjs(timeFrame.end).add(1, "week");
            break;
        case TimeRange.MONTH:
            endDate = dayjs(timeFrame.end).add(1, "month").endOf("month").second(0).millisecond(0);
            break;
        case TimeRange.YEAR:
            endDate = dayjs(timeFrame.end).add(1, "year");
            break;
        default:
            return getSwitchTimeFrameCustomRange(
                { start: dayjs(timeFrame.start).add(offset, "minute"), end: timeFrame.end },
                true,
            );
    }

    endDate = disableFuture ? dayjs.min(now, endDate) : endDate;

    return { start: startDate, end: endDate };
};

/**
 * Calculates next or previous date based on the given date, and
 * value and unit to add/subtract to get this new date
 *
 * @param date reference date
 * @param value amount of time unit to add/subtract to/from given date
 * @param unit time unit of value to add/subtract
 * @param isSwitchNext boolean indicating if value should be added (next)
 * or subtracted (previous)
 * @returns new next or previous date
 */
export const getSwitchDate = (date: Dayjs, value: number, unit: ManipulateType, isSwitchNext: boolean) => {
    const prevDate = date.subtract(value, unit);
    const nextDate = date.add(value, unit);
    const validNextDateTime = isBeforeToday(nextDate) ? nextDate : dayjs();
    return isSwitchNext ? validNextDateTime : prevDate;
};

/**
 * Returns next/previous timeFrame in relation to the given time frame
 *
 * @param timeFrame timeFrame used to calculate next/previous timeFrame
 * @param isSwitchNext boolean indicating if next/previous timeFrame should be calculated
 * @returns next/prev timeFrame
 */
export const getSwitchTimeFrameCustomRange = (timeFrame: TimeFrame, isSwitchNext = false) => {
    const startDate = dayjs(timeFrame.start);
    const endDate = dayjs(timeFrame.end);

    const timeStep = endDate.diff(startDate, "minute");
    return {
        start: getSwitchDate(startDate, timeStep, "minute", isSwitchNext),
        end: getSwitchDate(endDate, timeStep, "minute", isSwitchNext),
    };
};
